/** Action items: the regulator's dates, as a reminder beside what is waiting on the person.
 *
 * A button with a count that opens the dates a regime states (the CARB fee notice, the payment window,
 * how long to keep records), each with its source link. It is **not** an item waiting on anyone: it is
 * not in the bell's count or the table, because a date to watch for is not a task and counting it as
 * one would make "Waiting on you" untrue. It reads the obligations the Filings place reads, groups by
 * regime (`reminderDates`), and prints nothing when no regime in force holds any. A failed read is
 * said, with a retry (`ReadFailed`), never drawn as no dates: a reminder that cannot be read must not
 * look like nothing is coming.
 */

import { CalendarCheck } from "@phosphor-icons/react";
import { useState } from "react";

import { useObligations } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { KeyDatesList } from "../components/KeyDates";
import { Drawer } from "../components/ui/Drawer";
import { reminderDates } from "./datesReminders";
import styles from "./ApprovalsDates.module.css";

export function DatesReminder() {
  const obligations = useObligations();
  const [open, setOpen] = useState(false);
  if (obligations.error) {
    return (
      <ReadFailed
        of="The regulator's dates"
        error={obligations.error}
        onRetry={() => void obligations.refetch()}
      />
    );
  }
  if (!obligations.data) return null;
  const groups = reminderDates(obligations.data);
  if (groups.length === 0) return null;
  const count = groups.reduce((total, group) => total + group.items.length, 0);
  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-testid="dates-reminder"
      >
        <CalendarCheck size={14} aria-hidden="true" /> Dates ({count})
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title="Dates" width="md">
        {groups.map((group) => (
          <section key={group.regimeKey} className={styles.group} data-regime={group.regimeKey}>
            <h3 className={styles.heading}>{group.regimeName}</h3>
            <KeyDatesList items={group.items} testId={`dates-${group.regimeKey}`} />
          </section>
        ))}
      </Drawer>
    </>
  );
}
